import { KeyboardEvent, useState } from 'react'
import { faCheck, faPlus } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import {
  FieldContainer,
  FieldLabel,
  TagsRow,
  TagPill,
  TagRemoveButton,
  TagInput,
  SuggestionRow,
  SuggestionChip,
  HelperText,
} from './styled'
import { TagsInputProps } from './types'

/**
 * Free-text tag entry with optional preset suggestions. Used for fields
 * like cities where renters enter their own values but might benefit
 * from common picks. Enter or comma commits a tag; backspace on an empty
 * input removes the last tag.
 *
 * @param {TagsInputProps} props
 * @returns {JSX.Element}
 */
const TagsInput = ({
  label,
  value,
  onChange,
  placeholder,
  helperText,
  suggestions = [],
}: TagsInputProps): JSX.Element => {
  const [draft, setDraft] = useState<string>('')

  const commitDraft = (): void => {
    const trimmed = draft.trim()
    if (trimmed === '') {
      return
    }

    if (value.includes(trimmed)) {
      setDraft('')
      return
    }

    onChange([...value, trimmed])
    setDraft('')
  }

  const removeTag = (tag: string): void => {
    onChange(value.filter(existing => existing !== tag))
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commitDraft()
      return
    }

    if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  const handleSuggestionClick = (suggestion: string): void => {
    if (value.includes(suggestion)) {
      return
    }

    onChange([...value, suggestion])
  }

  return (
    <FieldContainer>
      <FieldLabel>{label}</FieldLabel>
      <TagsRow>
        {value.map(tag => (
          <TagPill key={tag}>
            {tag}
            <TagRemoveButton
              type="button"
              onClick={() => removeTag(tag)}
              aria-label={`Remove ${tag}`}
            >
              ×
            </TagRemoveButton>
          </TagPill>
        ))}
        <TagInput
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitDraft}
          placeholder={value.length === 0 ? placeholder : ''}
        />
      </TagsRow>
      {suggestions.length > 0 ? (
        <SuggestionRow>
          {suggestions.map(suggestion => {
            const isAlreadyAdded = value.includes(suggestion)
            return (
              <SuggestionChip
                key={suggestion}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                disabled={isAlreadyAdded}
              >
                <Icon icon={isAlreadyAdded ? faCheck : faPlus} /> {suggestion}
              </SuggestionChip>
            )
          })}
        </SuggestionRow>
      ) : null}
      {helperText !== undefined && helperText !== '' ? (
        <HelperText>{helperText}</HelperText>
      ) : null}
    </FieldContainer>
  )
}

export default TagsInput
